import type { Chart } from 'chart.js';

interface Point {
  x: number;
  y: number;
}

interface DispersionEllipse {
  center: Point;
  majorAxis: number;
  minorAxis: number;
  angle: number;
}

export const getDispersionEllipse = (points: Point[]): DispersionEllipse => {
  const n = points.length;
  if (n === 0) {
    return {
      center: { x: 0, y: 0 },
      majorAxis: 0,
      minorAxis: 0,
      angle: 0,
    };
  }

  const center = points.reduce(
    (acc, point) => ({
      x: acc.x + point.x,
      y: acc.y + point.y,
    }),
    { x: 0, y: 0 },
  );
  center.x /= n;
  center.y /= n;

  let covXX = 0;
  let covYY = 0;
  let covXY = 0;
  for (const point of points) {
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    covXX += dx * dx;
    covYY += dy * dy;
    covXY += dx * dy;
  }
  covXX /= n;
  covYY /= n;
  covXY /= n;

  const trace = covXX + covYY;
  const varianceDelta = covXX - covYY;
  const discriminantSquared = varianceDelta * varianceDelta + 4 * covXY * covXY;
  const discriminant = Math.sqrt(discriminantSquared < 0 ? 0 : discriminantSquared);

  let lambda1 = (trace + discriminant) / 2;
  let lambda2 = (trace - discriminant) / 2;
  lambda1 = Math.max(0, lambda1);
  lambda2 = Math.max(0, lambda2);

  let theta = varianceDelta !== 0 || covXY !== 0 ? Math.atan2(2 * covXY, varianceDelta) / 2 : 0;

  let majorAxis = 2.1459 * Math.sqrt(lambda1);
  let minorAxis = 2.1459 * Math.sqrt(lambda2);

  if (
    !Number.isFinite(center.x) ||
    !Number.isFinite(center.y) ||
    !Number.isFinite(majorAxis) ||
    !Number.isFinite(minorAxis) ||
    !Number.isFinite(theta)
  ) {
    return {
      center,
      majorAxis: 0,
      minorAxis: 0,
      angle: 0,
    };
  }

  return {
    center,
    majorAxis,
    minorAxis,
    angle: theta,
  };
};

/**
 * KWSA-9 Bug 2: Draw a dispersion boundary only for datasets with **3+** valid
 * plotted shots. With **exactly 2** shots, no colored ellipse should be rendered.
 */
const DISPERSION_MIN_SHOTS_FOR_ELLIPSE = 3;

/** Extra padding (px) so the drawn ellipse clears the outermost scatter points visually */
const ELLIPSE_PIXEL_PADDING = 10;

type ScatterPoint = { x: number; y: number };

function isFiniteScatterPoint(shot: unknown): shot is ScatterPoint {
  return (
    shot !== null &&
    typeof shot === 'object' &&
    Number.isFinite((shot as ScatterPoint).x) &&
    Number.isFinite((shot as ScatterPoint).y)
  );
}

export const generateDispersionEllipse = (chart: Chart): void => {
  const ctx = chart.ctx;

  const xAxis = chart.scales.x;
  const yAxis = chart.scales.y;

  chart.data.datasets.forEach(dataset => {
    const club = dataset as { data: unknown[]; backgroundColor?: string | CanvasGradient };
    if (!club.data || !Array.isArray(club.data) || club.data.length < DISPERSION_MIN_SHOTS_FOR_ELLIPSE) {
      return;
    }

    const pixelValuesOfData = club.data
      .filter(isFiniteScatterPoint)
      .map((shot: ScatterPoint) => {
        const px = xAxis.getPixelForValue(shot.x);
        const py = yAxis.getPixelForValue(shot.y);
        return { x: px, y: py };
      })
      .filter((p: Point) => Number.isFinite(p.x) && Number.isFinite(p.y));

    if (pixelValuesOfData.length < DISPERSION_MIN_SHOTS_FOR_ELLIPSE) {
      return;
    }

    const dispersionEllipse = getDispersionEllipse(pixelValuesOfData);

    let { center, majorAxis, minorAxis, angle } = dispersionEllipse;

    if (
      !Number.isFinite(center.x) ||
      !Number.isFinite(center.y) ||
      !Number.isFinite(majorAxis) ||
      !Number.isFinite(minorAxis) ||
      !Number.isFinite(angle)
    ) {
      return;
    }

    majorAxis += ELLIPSE_PIXEL_PADDING;
    minorAxis += ELLIPSE_PIXEL_PADDING;

    if (!Number.isFinite(majorAxis) || !Number.isFinite(minorAxis)) {
      return;
    }

    ctx.save();
    try {
      const lineColor =
        typeof club.backgroundColor === 'string' ? club.backgroundColor : 'rgba(255,255,255,0.6)';
      const lineWidth = 2;

      ctx.strokeStyle = lineColor;
      ctx.lineWidth = lineWidth;
      ctx.beginPath();

      for (let i = 0; i < 2 * Math.PI; i += 0.01) {
        const x = center.x + majorAxis * Math.cos(i) * Math.cos(angle) - minorAxis * Math.sin(i) * Math.sin(angle);
        const y = center.y + majorAxis * Math.cos(i) * Math.sin(angle) + minorAxis * Math.sin(i) * Math.cos(angle);

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }

      ctx.closePath();
      ctx.stroke();
    } finally {
      ctx.restore();
    }
  });
};
